import * as React from 'react';
import { useCallback, useMemo } from 'react';

import BulkActionsDropdown from 'components/common/EntityDataTable/BulkActionsDropdown';
import { useModalContext } from 'common/contexts';
import type { Event } from 'components/events/events/types';
import useEventBulkActions from 'components/events/events/hooks/useEventBulkActions';
import useSecurityEventBulkActions from 'security-app/components/SecurityEvents/Alerts/useSecurityEventBulkActions';
import MenuItem from 'components/bootstrap/menuitem/MenuItem';
import useSendEventActionTelemetry from 'components/events/events/hooks/useSendEventActionTelemetry';
import { isPermitted } from 'util/PermissionsMixin';
import useCurrentUser from 'hooks/useCurrentUser';

type Props = {
  selectedEntitiesData: { [eventId: string]: Event };
};

const BulkActions = ({ selectedEntitiesData }: Props) => {
  const { setEntity, setModal } = useModalContext();
  const currentUser = useCurrentUser();
  const selectedEvents = useMemo(() => Object.values(selectedEntitiesData), [selectedEntitiesData]);
  const sendEventActionTelemetry = useSendEventActionTelemetry();
  const { actions, pluggableActionModals } = useEventBulkActions(selectedEvents);
  const { actions: securityEventActions, pluggableActionModals: securityEventModals } =
    useSecurityEventBulkActions(selectedEvents);

  const replayableEvents = selectedEvents.filter(
    (event) => !!event.replay_info && isPermitted(currentUser?.permissions, `eventdefinitions:read:${event.id}`),
  );
  const onReplaySearchClick = useCallback(() => {
    sendEventActionTelemetry('REPLAY_SEARCH', true, { events_length: replayableEvents.length });
    setEntity(replayableEvents);
    setModal('BULK-REPLAY-SEARCH-VIEW');
  }, [replayableEvents, sendEventActionTelemetry, setEntity, setModal]);

  return (
    <>
      <BulkActionsDropdown>
        {replayableEvents.length > 0 && <MenuItem onClick={onReplaySearchClick}>Replay Search</MenuItem>}
        {actions}
        {securityEventActions}
      </BulkActionsDropdown>
      {pluggableActionModals}
      {securityEventModals}
    </>
  );
};

export default BulkActions;
